import React, { useMemo } from "react";
import { max } from "lodash";
import styled, { createGlobalStyle } from "styled-components";
import {
  Elevation,
  GlossyThemeProvider,
  Radius,
  Spacing,
  ZIndexes,
  background,
  cover,
} from "@glossy";
import { form } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import Button from "@ui-kit/button";
import { tr } from "@radarly/i18n";

interface IProps {
  children: React.ReactNode;
  onClose: () => void;
  onPageRight: () => void;
  onPageLeft: () => void;
  canPageLeft: boolean;
  canPageRight: boolean;
  paginationLabel: string;
  locale: Locale;
  isBelowBanner: boolean;
}

const GlobalStyle = createGlobalStyle`
  #main {
    overflow: hidden;
  }
`;

const Root = styled.div`
  ${cover()};
  top: 130px;
  z-index: ${ZIndexes.Content};
  background-color: ${background};
  padding: ${Spacing.Large} ${Spacing.Jumbo};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Large};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.Small};
`;

const Cluster = styled.div`
  padding: ${Spacing.Large};
  border-radius: ${Radius.Small};
  box-shadow: ${Elevation.Elevation_2};
  background-color: ${background};
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PaginationLabel = styled.div`
  color: ${form};
`;

const BANNER_HEIGHT = 50;
const HEADER_HEIGHT = 60;

const RadarlyPanelsImageCluster = ({
  children,
  onClose,
  onPageRight,
  onPageLeft,
  canPageLeft,
  canPageRight,
  paginationLabel,
  locale,
  isBelowBanner,
}: IProps) => {
  const main = document.getElementById("main");

  const scrollTop = main.scrollTop;
  const top = useMemo(
    () =>
      max([
        isBelowBanner
          ? 130 +
            BANNER_HEIGHT -
            (scrollTop < HEADER_HEIGHT ? 0 : HEADER_HEIGHT)
          : 130 - scrollTop,
        70,
      ]),
    [scrollTop, isBelowBanner]
  );

  return (
    <GlossyThemeProvider name="tile">
      <GlobalStyle />
      <Root style={{ top }}>
        <Header>
          <Button
            icon="arrow-left"
            onClick={onClose}
            size="m"
            text={tr(locale, "IMAGE_CLUSTER::BACK_TO_LIST")}
            variant="tertiary"
          />
          <Pagination>
            {canPageLeft && (
              <Button
                icon="chevron-left"
                size="m"
                onClick={onPageLeft}
                text={tr(locale, "IMAGE_CLUSTER::PREVIOUS")}
                variant="tertiary"
              />
            )}
            <PaginationLabel>{paginationLabel}</PaginationLabel>
            {canPageRight && (
              <Button
                icon="chevron-right"
                iconRight
                size="m"
                onClick={onPageRight}
                text={tr(locale, "IMAGE_CLUSTER::NEXT")}
                variant="tertiary"
              />
            )}
          </Pagination>
        </Header>
        <Cluster>{children}</Cluster>
      </Root>
    </GlossyThemeProvider>
  );
};

export default RadarlyPanelsImageCluster;
